/* eslint-disable @typescript-eslint/no-redeclare */
import {
  Box,
  CircularProgress,
  Button as MuiButton,
  PaletteMode,
  SxProps,
  Theme,
  Typography,
  useTheme
} from "@mui/material";
import React, { CSSProperties, ElementType, FC, PropsWithChildren } from "react";
import { colors } from "../../theme/colors";
import { useGradient } from "../../utils/useGradient";

export type ButtonVariant = "primary" | "secondary" | "accent";
export const ButtonVariant = {
  primary: "primary" as ButtonVariant,
  secondary: "secondary" as ButtonVariant,
  accent: "accent" as ButtonVariant
};

export type ButtonSize = "auto" | "full";
export const ButtonSize = {
  auto: "auto" as ButtonSize,
  full: "full" as ButtonSize
};
export type ButtonProps = {
  label?: string;
  title?: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  forceThemeMode?: PaletteMode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseDown?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseUp?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  sx?: SxProps<Theme>;
  type?: "submit" | "button";
  disabled?: boolean;
  href?: string;
  loading?: boolean;
  /** Text shown next to the spinner while `loading` (e.g. "Download in progress"). */
  loadingLabel?: string;
  id?: string;
  // Navigation/accessibility props for rendering as link
  component?: ElementType;
  to?: string;
  target?: string;
  rel?: string;
  tabIndex?: number;
  "aria-current"?: "page" | "step" | "location" | "date" | "time" | "true" | "false";
  "aria-label"?: string;
  "aria-expanded"?: boolean;
};

const Button: FC<PropsWithChildren<ButtonProps>> = ({
  title,
  label,
  startIcon,
  endIcon,
  variant = "primary",
  size = "auto",
  type = "button",
  forceThemeMode,
  onClick,
  onMouseDown,
  onMouseUp,
  sx,
  disabled = false,
  href,
  loading,
  loadingLabel,
  children,
  id,
  component,
  to,
  target,
  rel,
  tabIndex,
  "aria-current": ariaCurrent,
  "aria-label": ariaLabel,
  "aria-expanded": ariaExpanded
}) => {
  const theme = useTheme();

  const themeMode = forceThemeMode ?? theme.palette.mode;
  const gradient = useGradient("horizontal", forceThemeMode);

  const stylesMap: { [key: string]: CSSProperties } = {
    primary: {
      color: colors.white,
      background: gradient
    },
    secondary: {
      color: themeMode === "dark" ? colors.white : colors.brown,
      backgroundColor: "transparent",
      border: `3px solid ${themeMode === "dark" ? colors.white : colors.brown}`
    },
    accent: {
      color: colors.white,
      backgroundColor: "transparent",
      border: `3px solid ${colors.white}`
    }
  };

  return (
    <MuiButton
      id={id}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      type={type}
      sx={[
        {
          textDecoration: "none",
          borderRadius: 3,
          filter: "drop-shadow(-4px 5px 4px rgba(0, 0, 0, 0.25))",
          ":hover": {
            opacity: 0.8
          },
          minHeight: 50,
          minWidth: 50,
          width: size === "full" ? "100%" : "auto",
          ...stylesMap[variant]
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : [])
      ]}
      title={title}
      href={href}
      disabled={disabled || loading}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      component={component as any}
      to={to}
      target={target}
      rel={rel}
      tabIndex={tabIndex}
      aria-current={ariaCurrent}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      aria-busy={loading || undefined}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        {!loading && (
          <>
            {startIcon && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  "& svg, & img": {
                    width: theme.spacing(5),
                    height: theme.spacing(5)
                  }
                }}
                mr={label && 2}>
                {startIcon}
              </Box>
            )}
            {label && (
              <Typography sx={{ mx: { xs: 1.5, md: 2 } }} fontWeight="900">
                {label}
              </Typography>
            )}
            {children}
            {endIcon && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  "& svg": {
                    width: theme.spacing(5),
                    height: theme.spacing(5)
                  }
                }}
                ml={label && 2}>
                {endIcon}
              </Box>
            )}
          </>
        )}
        {loading && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center"
            }}>
            <CircularProgress
              size={theme.spacing(5)}
              sx={{
                color: stylesMap[variant].color
              }}
            />
            {loadingLabel && (
              <Typography sx={{ ml: 2, color: stylesMap[variant].color }} fontWeight="900">
                {loadingLabel}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </MuiButton>
  );
};

export default Button;
